"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
class ConnectionHealthData {
    constructor() {
        this.connectionStartTimestampMs = 0;
        this.consecutiveStatsWithNoPackets = 0;
        this.consecutiveStatsWithNoAudioPacketsSent = 0;
        this.lastPacketLossInboundTimestampMs = 0;
        this.lastGoodSignalTimestampMs = 0;
        this.lastWeakSignalTimestampMs = 0;
        this.lastNoSignalTimestampMs = 0;
        this.consecutiveMissedPongs = 0;
        this.packetsReceivedInLastMinute = [];
        this.fractionPacketsLostInboundInLastMinute = [];
        this.audioSpeakerDelayMs = 0;
        this.isVideoEncoderHardware = false;
        this.videoEncodingTimeInMs = 0;
        this.videoEncodingTimePerFrameInMs = 0;
        this.cpuLimitationDuration = 0;
        this.videoInputFps = 0;
        this.videoEncodeFps = 0;
        this.isContentShare = false;
        this.numberOfPublishedVideoSources = 0;
        this.connectionStartTimestampMs = Date.now();
        this.lastGoodSignalTimestampMs = Date.now();
    }
    static isTimestampRecent(timestampMs, recentDurationMs) {
        return Date.now() < timestampMs + recentDurationMs;
    }
    setConnectionStartTime() {
        this.connectionStartTimestampMs = Date.now();
        this.lastGoodSignalTimestampMs = Date.now();
    }
    reset() {
        this.connectionStartTimestampMs = 0;
        this.consecutiveStatsWithNoPackets = 0;
        this.consecutiveStatsWithNoAudioPacketsSent = 0;
        this.lastPacketLossInboundTimestampMs = 0;
        this.lastGoodSignalTimestampMs = 0;
        this.lastWeakSignalTimestampMs = 0;
        this.lastNoSignalTimestampMs = 0;
        this.consecutiveMissedPongs = 0;
        this.packetsReceivedInLastMinute = [];
        this.fractionPacketsLostInboundInLastMinute = [];
        this.audioSpeakerDelayMs = 0;
        this.connectionStartTimestampMs = Date.now();
        this.lastGoodSignalTimestampMs = Date.now();
        this.isVideoEncoderHardware = false;
        this.videoEncodingTimeInMs = 0;
        this.videoEncodingTimePerFrameInMs = 0;
        this.cpuLimitationDuration = 0;
        this.videoInputFps = 0;
        this.videoEncodeFps = 0;
        this.isContentShare = false;
        this.numberOfPublishedVideoSources = 0;
    }
    isConnectionStartRecent(recentDurationMs) {
        return ConnectionHealthData.isTimestampRecent(this.connectionStartTimestampMs, recentDurationMs);
    }
    isLastPacketLossRecent(recentDurationMs) {
        return ConnectionHealthData.isTimestampRecent(this.lastPacketLossInboundTimestampMs, recentDurationMs);
    }
    isGoodSignalRecent(recentDurationMs) {
        return ConnectionHealthData.isTimestampRecent(this.lastGoodSignalTimestampMs, recentDurationMs);
    }
    isWeakSignalRecent(recentDurationMs) {
        return ConnectionHealthData.isTimestampRecent(this.lastWeakSignalTimestampMs, recentDurationMs);
    }
    isNoSignalRecent(recentDurationMs) {
        return ConnectionHealthData.isTimestampRecent(this.lastNoSignalTimestampMs, recentDurationMs);
    }
    clone() {
        const cloned = new ConnectionHealthData();
        cloned.connectionStartTimestampMs = this.connectionStartTimestampMs;
        cloned.consecutiveStatsWithNoPackets = this.consecutiveStatsWithNoPackets;
        cloned.consecutiveStatsWithNoAudioPacketsSent = this.consecutiveStatsWithNoAudioPacketsSent;
        cloned.lastPacketLossInboundTimestampMs = this.lastPacketLossInboundTimestampMs;
        cloned.lastGoodSignalTimestampMs = this.lastGoodSignalTimestampMs;
        cloned.lastWeakSignalTimestampMs = this.lastWeakSignalTimestampMs;
        cloned.lastNoSignalTimestampMs = this.lastNoSignalTimestampMs;
        cloned.consecutiveMissedPongs = this.consecutiveMissedPongs;
        cloned.packetsReceivedInLastMinute = this.packetsReceivedInLastMinute.slice(0);
        cloned.fractionPacketsLostInboundInLastMinute = this.fractionPacketsLostInboundInLastMinute.slice(0);
        cloned.audioSpeakerDelayMs = this.audioSpeakerDelayMs;
        cloned.isVideoEncoderHardware = this.isVideoEncoderHardware;
        cloned.videoEncodingTimeInMs = this.videoEncodingTimeInMs;
        cloned.videoEncodingTimePerFrameInMs = this.videoEncodingTimePerFrameInMs;
        cloned.cpuLimitationDuration = this.cpuLimitationDuration;
        cloned.videoInputFps = this.videoInputFps;
        cloned.videoEncodeFps = this.videoEncodeFps;
        cloned.isContentShare = this.isContentShare;
        cloned.numberOfPublishedVideoSources = this.numberOfPublishedVideoSources;
        return cloned;
    }
    setConsecutiveMissedPongs(pongs) {
        this.consecutiveMissedPongs = pongs;
    }
    setConsecutiveStatsWithNoPackets(stats) {
        this.consecutiveStatsWithNoPackets = stats;
    }
    setConsecutiveStatsWithNoAudioPacketsSent(stats) {
        this.consecutiveStatsWithNoAudioPacketsSent = stats;
    }
    setLastPacketLossInboundTimestampMs(timeStamp) {
        this.lastPacketLossInboundTimestampMs = timeStamp;
    }
    setLastNoSignalTimestampMs(timeStamp) {
        this.lastNoSignalTimestampMs = timeStamp;
    }
    setLastWeakSignalTimestampMs(timeStamp) {
        this.lastWeakSignalTimestampMs = timeStamp;
    }
    setLastGoodSignalTimestampMs(timeStamp) {
        this.lastGoodSignalTimestampMs = timeStamp;
    }
    setAudioSpeakerDelayMs(delayMs) {
        this.audioSpeakerDelayMs = delayMs;
    }
    setIsVideoEncoderHardware(isHardware) {
        this.isVideoEncoderHardware = isHardware;
    }
    setVideoEncodingTimeInMs(stats) {
        this.videoEncodingTimeInMs = stats;
    }
    setVideoEncodingTimePerFrameInMs(stats) {
        this.videoEncodingTimePerFrameInMs = stats;
    }
    setCpuLimitationDuration(stats) {
        this.cpuLimitationDuration = stats;
    }
    setVideoInputFps(stats) {
        this.videoInputFps = stats;
    }
    setVideoEncodeFps(stats) {
        this.videoEncodeFps = stats;
    }
    setIsContentShare(isContentShare) {
        this.isContentShare = isContentShare;
    }
    setNumberOfPublishedVideoSources(stats) {
        this.numberOfPublishedVideoSources = stats;
    }
}
exports.default = ConnectionHealthData;
//# sourceMappingURL=ConnectionHealthData.js.map